import { useClavesSatConsultadas } from "@/features/catalogos/hooks/use-claves-sat";
import { AvisoProrrateoAutomatico } from "./AvisoProrrateoAutomatico";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useProrrateo } from "@/features/prorrateo/hooks/use-prorrateo";
import { useRederivarCuentasFaltantes } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import { calcularDiferencia } from "@/features/prorrateo/utils/suma";
import { evaluarConfirmable } from "@/features/prorrateo/utils/conceptos";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Group, PageHeader, ReadOnlyBanner, Stack, Text } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/prorrateo";
import { ConceptosTable } from "./ConceptosTable";
import { DispersionExcelMenu } from "./DispersionExcelMenu";
import { ProrrateoActions } from "./ProrrateoActions";
import { ProrrateoSociedadProvider } from "./ProrrateoSociedadContext";
import { SumaResumenBanner } from "./SumaResumenBanner";

/**
 * Container del prorrateo · orquesta los hooks de datos y compone los
 * subcomponentes presentacionales. No tiene lógica de negocio propia ·
 * todo lo derivado vive en `calcularDiferencia` / `estaPostEnvio` /
 * las mutations.
 */
export function ProrrateoPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const { data: prorrateo } = useProrrateo(id);

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (P29) · sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const confirmado = prorrateo.estado === "Confirmado";
  const bloqueado = confirmado || soloLectura;
  const diferencia = calcularDiferencia(prorrateo.sumaTotal, sol.cfdi?.subtotal ?? 0);

  // Al abrir el prorrateo, si hay líneas automáticas "Sin cuenta" (su clave no
  // tenía mapeo al construirse), intentamos re-derivar la cuenta UNA vez de forma
  // transparente: así, cuando el admin agrega la cuenta solicitada, la línea la
  // toma sola sin que el usuario re-haga ningún paso ni pierda su CeCo/importe.
  const rederivar = useRederivarCuentasFaltantes(id);
  const rederivado = useRef(false);
  const hayLineasSinCuenta = prorrateo.lineas.some((l) => !l.cuentaContableCodigo);
  useEffect(() => {
    if (rederivado.current || bloqueado || !hayLineasSinCuenta) return;
    rederivado.current = true;
    rederivar.mutate();
  }, [bloqueado, hayLineasSinCuenta, rederivar]);
  // Gate de confirmación (cuadre POR CONCEPTO + CeCo/cuenta en cada sub-línea, sin ceros) ·
  // el backend valida lo mismo. El cuadre por concepto implica el cuadre del total.
  const { catalogadas } = useClavesSatConsultadas(prorrateo.lineas.map((l) => l.claveSat));
  const confirmable = evaluarConfirmable(prorrateo.lineas, catalogadas);
  const puedeConfirmar = confirmable.ok;
  const motivoNoConfirmable = confirmable.motivo;

  return (
    <ProrrateoSociedadProvider numeroSociedad={sol.organizacion?.numeroSociedad} moneda={sol.cfdi?.moneda ?? sol.invoice?.moneda ?? sol.contratoAnualizado?.moneda ?? sol.ordenCompra?.moneda}>
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Prorrateo"
        description={`Estado: ${prorrateo.estado} · ${prorrateo.lineas.length} línea(s)`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Prorrateo" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="prorrateo" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="El prorrateo no puede modificarse." />}
          <AvisoProrrateoAutomatico prorrateo={prorrateo} />
          <SumaResumenBanner diferencia={diferencia} />
          {!bloqueado && prorrateo.dispersionExcelHabilitada && (
            <Group justify="space-between" align="center" className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <Text size="sm" c="dimmed">
                Puedes cargar el desglose por concepto/centro de costo desde una plantilla de Excel.
              </Text>
              <DispersionExcelMenu solicitudId={id} lineas={prorrateo.lineas} />
            </Group>
          )}
          <ConceptosTable lineas={prorrateo.lineas} disabled={bloqueado} solicitudId={id} numeroSolicitud={sol.numeroSolicitud} validarClavesSat permiteProrratear={sol.tipo !== "OrdenDeCompra"} />
          <ProrrateoActions
            solicitudId={id}
            confirmado={confirmado}
            soloLectura={soloLectura}
            puedeReabrir={!prorrateo.confirmadoAutomaticamente}
            puedeConfirmar={puedeConfirmar}
            motivoNoConfirmable={motivoNoConfirmable}
            cantidadLineas={prorrateo.lineas.length}
            onVolver={() => navigate({ to: "/solicitudes/$id/proveedor", params: { id } })}
            onConfirmado={() => navigate({ to: "/solicitudes/$id/documentos", params: { id } })}
          />
        </Stack>
      </LayoutConStepper>
    </div>
    </ProrrateoSociedadProvider>
  );
}
